import React, { useEffect, useState } from "react";
import { 
  Button, 
  Box, 
  useColorModeValue, 
  Select, 
  Flex, 
  Heading,
  Text, 
  Badge,
  SimpleGrid,
  Icon,
  HStack,
  VStack,
  Container,
  Image
} from "@chakra-ui/react";
import { db } from "../firebase.config";
import { collection, getDocs, query, where, limit } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import FadeSpinner from "../components/feedback/FadeSpinner";
import { 
  FaSortAmountUpAlt, 
  FaSortAmountDownAlt,
  FaMapMarkerAlt,
  FaBed,
  FaBath,
  FaRulerCombined
} from "react-icons/fa";
import { 
  GiModernCity,
  GiOfficeChair,
  GiFamilyHouse,
  GiVillage,
  GiIsland
} from "react-icons/gi";
import { BsBuilding } from "react-icons/bs";
import { HiOutlineHomeModern } from "react-icons/hi2";
import moment from "moment";
import { Helmet } from "react-helmet";

const SeeAllFeaturedProperties = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortBy, setSortBy] = useState("date");
  const [filterType, setFilterType] = useState("featured");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      try {
        let propertiesQuery;
        
        if (filterType === "featured") {
          propertiesQuery = query(
            collection(db, "properties"),
            where("featured", "==", true),
            limit(100)
          );
        } else if (filterType === "sale") {
          propertiesQuery = query(
            collection(db, "properties"),
            where("type", "==", "sale"),
            limit(100)
          );
        } else if (filterType === "rent") {
          propertiesQuery = query(
            collection(db, "properties"),
            where("type", "==", "rent"),
            limit(100)
          );
        } else {
          propertiesQuery = query(collection(db, "properties"), limit(100));
        }

        const propertiesSnapshot = await getDocs(propertiesQuery);
        const propertyListings = propertiesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const sortedProperties = propertyListings.sort((a, b) => {
          if (sortBy === "price") {
            return sortOrder === "asc" 
              ? (a.discountedPrice || a.price) - (b.discountedPrice || b.price)
              : (b.discountedPrice || b.price) - (a.discountedPrice || a.price);
          } else if (sortBy === "date") {
            return sortOrder === "asc"
              ? a.timestamp?.toDate() - b.timestamp?.toDate()
              : b.timestamp?.toDate() - a.timestamp?.toDate();
          } else if (sortBy === "area") {
            return sortOrder === "asc"
              ? (a.area || 0) - (b.area || 0)
              : (b.area || 0) - (a.area || 0);
          }
          return 0;
        });
        
        setProperties(sortedProperties);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching properties:", err);
        setError("Failed to fetch properties. Please try again later.");
        setLoading(false);
      }
    };

    fetchProperties();
  }, [sortOrder, sortBy, filterType]);

  const getPropertyIcon = (type) => {
    const propertyIcons = {
      "apartment": <BsBuilding size={14} />,
      "house": <GiFamilyHouse size={14} />,
      "villa": <HiOutlineHomeModern size={14} />,
      "condo": <GiModernCity size={14} />,
      "townhouse": <GiFamilyHouse size={14} />,
      "plot": <GiVillage size={14} />,
      "office": <GiOfficeChair size={14} />,
      "retail": <GiOfficeChair size={14} />,
      "warehouse": <GiOfficeChair size={14} />,
      "industrial": <GiOfficeChair size={14} />,
      "commercial-plot": <GiVillage size={14} />
    };
    return propertyIcons[type] || <GiFamilyHouse size={14} />;
  };

  const getPropertyTypeLabel = (type) => {
    const labels = {
      "apartment": "Apartment",
      "house": "House",
      "villa": "Villa",
      "condo": "Condo",
      "townhouse": "Townhouse",
      "plot": "Plot",
      "office": "Office",
      "retail": "Retail",
      "warehouse": "Warehouse",
      "industrial": "Industrial",
      "commercial-plot": "Commercial Plot"
    };
    return labels[type] || "Property";
  };

  const checkDetails = (id, type = "sale") => {
    navigate(`/category/${type}/${id}`);
  };

  const formatPrice = (price) => {
    if (!price) return "N/A";
    const numPrice = parseInt(price);
    if (numPrice >= 10000000) {
      return `PKR ${(numPrice / 10000000).toFixed(1)} Cr`;
    } else if (numPrice >= 100000) {
      return `PKR ${(numPrice / 100000).toFixed(1)} L`;
    } else {
      return `PKR ${numPrice.toLocaleString()}`;
    }
  };

  if (loading) return <FadeSpinner />;
  
  if (error) return (
    <Box className="text-center p-8">
      <Heading size="lg" color="red.500" mb={4}>{error}</Heading>
      <Button onClick={() => window.location.reload()} colorScheme="blue">
        Try Again
      </Button>
    </Box>
  );

  return (
    <>
      <Helmet>
        <title>Featured Properties | EstateGhar Real Estate</title>
        <meta
          name="description"
          content="Browse our featured properties for sale and rent. Find apartments, houses, villas, commercial spaces, and plots in prime locations."
        />
        <meta
          name="keywords"
          content="real estate, properties for sale, properties for rent, apartments, houses, villas, commercial property, residential plots"
        />
      </Helmet>

      <Box minH="100vh" bg="gray.50" py={6}>
        <Container maxW="7xl">
          {/* Header */}
          <VStack spacing={4} mb={8} textAlign="center">
            <Heading as="h1" size="xl" color="gray.800" fontWeight="bold">
              Featured Properties
            </Heading>
            <Box w="80px" h="1px" bg="teal.500" />
            <Text fontSize="md" color="gray.600" maxW="xl">
              Discover premium properties in prime locations
            </Text>
          </VStack>

          {/* Filters */}
          <Box 
            bg={cardBg} 
            p={4} 
            borderRadius="lg" 
            shadow="sm"
            borderWidth="1px"
            borderColor={borderColor}
            mb={6}
          >
            <Flex 
              direction={{ base: "column", md: "row" }} 
              justify="space-between" 
              align={{ base: "stretch", md: "center" }}
              gap={3}
            >
              <HStack spacing={2} flexWrap="wrap" mb={{ base: 3, md: 0 }}>
                <Button
                  size="xs"
                  onClick={() => setFilterType("all")}
                  colorScheme={filterType === "all" ? "teal" : "gray"}
                  variant={filterType === "all" ? "solid" : "outline"}
                  borderRadius="full"
                >
                  All
                </Button>
                <Button
                  size="xs"
                  onClick={() => setFilterType("featured")}
                  colorScheme={filterType === "featured" ? "teal" : "gray"}
                  variant={filterType === "featured" ? "solid" : "outline"}
                  borderRadius="full"
                >
                  Featured
                </Button>
                <Button
                  size="xs"
                  onClick={() => setFilterType("sale")}
                  colorScheme={filterType === "sale" ? "teal" : "gray"}
                  variant={filterType === "sale" ? "solid" : "outline"}
                  borderRadius="full"
                >
                  For Sale
                </Button>
                <Button
                  size="xs"
                  onClick={() => setFilterType("rent")}
                  colorScheme={filterType === "rent" ? "teal" : "gray"}
                  variant={filterType === "rent" ? "solid" : "outline"}
                  borderRadius="full"
                >
                  For Rent
                </Button>
              </HStack>

              <Flex align="center" gap={2} flexShrink={0}>
                <Icon as={sortOrder === "asc" ? FaSortAmountUpAlt : FaSortAmountDownAlt} size={14} />
                <Select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [newSortBy, newSortOrder] = e.target.value.split("-");
                    setSortBy(newSortBy);
                    setSortOrder(newSortOrder);
                  }}
                  width="180px"
                  size="sm"
                  borderRadius="md"
                  borderColor="teal.300"
                  focusBorderColor="teal.500"
                >
                  <option value="date-desc">Newest First</option>
                  <option value="date-asc">Oldest First</option>
                  <option value="price-desc">Price: High to Low</option>
                  <option value="price-asc">Price: Low to High</option>
                  <option value="area-desc">Area: Large to Small</option>
                  <option value="area-asc">Area: Small to Large</option>
                </Select>
              </Flex>
            </Flex>
          </Box>

          {/* Results Count */}
          <Text mb={4} fontSize="sm" color="gray.600">
            {properties.length} propert{properties.length === 1 ? "y" : "ies"} found
          </Text>

          {/* Properties Grid - 4 cards per row */}
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing={5}>
            {properties.map((property) => (
              <Box
                key={property.id}
                bg={cardBg}
                borderRadius="xl"
                overflow="hidden"
                borderWidth="1px"
                borderColor={borderColor}
                transition="all 0.2s ease"
                _hover={{
                  transform: "translateY(-2px)",
                  shadow: "lg",
                  borderColor: "teal.300",
                }}
                shadow="sm"
                height="100%"
                display="flex"
                flexDirection="column"
              >
                {/* Image Section */}
                <Box position="relative" height="180px" overflow="hidden">
                  <Image
                    src={property.propertyImages?.[0] || "/placeholder-property.jpg"}
                    alt={property.title}
                    w="100%"
                    h="100%"
                    objectFit="cover"
                    transition="transform 0.3s ease"
                    _hover={{ transform: "scale(1.05)" }}
                  />
                  
                  {/* Property Type Badge */}
                  <Badge
                    position="absolute"
                    top={2}
                    left={2}
                    bg="white"
                    color="teal.700"
                    px={2}
                    py={1}
                    borderRadius="md"
                    fontSize="xs"
                    fontWeight="semibold"
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    {getPropertyIcon(property.propertyType)}
                    {getPropertyTypeLabel(property.propertyType)}
                  </Badge>
                  
                  {/* Listing Type Badge */}
                  <Badge
                    position="absolute"
                    top={2}
                    right={2}
                    bg={property.type === "sale" ? "green.500" : "blue.500"}
                    color="white"
                    px={2}
                    py={1}
                    borderRadius="md"
                    fontSize="xs"
                    fontWeight="bold"
                  >
                    {property.type === "sale" ? "SALE" : "RENT"}
                  </Badge>

                  {/* Featured Badge */}
                  {property.featured && (
                    <Badge
                      position="absolute"
                      bottom={2}
                      left={2}
                      bg="yellow.400"
                      color="gray.800"
                      px={2}
                      py={1}
                      borderRadius="md"
                      fontSize="xs"
                      fontWeight="bold"
                    >
                      ⭐
                    </Badge>
                  )}
                </Box>

                {/* Content Section */}
                <VStack 
                  align="stretch" 
                  p={4} 
                  spacing={3} 
                  flex={1}
                  justify="space-between"
                >
                  {/* Title and Price */}
                  <Box flex={1}>
                    <Heading 
                      size="sm" 
                      color="gray.800" 
                      mb={2} 
                      noOfLines={2}
                      lineHeight="tight"
                    >
                      {property.title}
                    </Heading>
                    
                    <Flex justify="space-between" align="center" mb={2}>
                      <Text fontSize="lg" fontWeight="bold" color="teal.600">
                        {formatPrice(property.discountedPrice || property.price)}
                      </Text>
                      {property.offer && property.discountedPrice && (
                        <Text fontSize="xs" color="gray.500" textDecoration="line-through">
                          {formatPrice(property.price)}
                        </Text>
                      )}
                    </Flex>

                    {/* Location */}
                    <Flex align="center" color="gray.600" mb={2}>
                      <Icon as={FaMapMarkerAlt} mr={1} size={12} />
                      <Text fontSize="xs" noOfLines={1}>
                        {property.city || property.location || "Location not specified"}
                      </Text>
                    </Flex>
                  </Box>

                  {/* Property Specs */}
                  <SimpleGrid columns={3} spacing={2} mb={2}>
                    <VStack spacing={0}>
                      <Flex align="center" gap={1}>
                        <Icon as={FaBed} color="teal.500" size={12} />
                        <Text fontSize="xs" color="gray.600">Beds</Text>
                      </Flex>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                        {property.bedrooms || "-"}
                      </Text>
                    </VStack>
                    
                    <VStack spacing={0}>
                      <Flex align="center" gap={1}>
                        <Icon as={FaBath} color="teal.500" size={12} />
                        <Text fontSize="xs" color="gray.600">Baths</Text>
                      </Flex>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                        {property.bathrooms || "-"}
                      </Text>
                    </VStack>
                    
                    <VStack spacing={0}>
                      <Flex align="center" gap={1}>
                        <Icon as={FaRulerCombined} color="teal.500" size={12} />
                        <Text fontSize="xs" color="gray.600">Area</Text>
                      </Flex>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                        {property.area ? `${property.area} sq ft` : "-"}
                      </Text>
                    </VStack>
                  </SimpleGrid>

                  {/* Timestamp */}
                  {property.timestamp && (
                    <Text fontSize="xs" color="gray.500">
                      {moment(property.timestamp?.toDate()).fromNow()}
                    </Text>
                  )}

                  {/* View Details Button */}
                  <Button
                    onClick={() => checkDetails(property.id, property.type)}
                    colorScheme="teal"
                    size="sm"
                    w="full"
                    borderRadius="md"
                    mt={1}
                    variant="outline"
                    _hover={{
                      bg: "teal.50",
                      borderColor: "teal.400",
                    }}
                  >
                    View Details
                  </Button>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>

          {/* No Results */}
          {properties.length === 0 && !loading && (
            <Box textAlign="center" py={12}>
              <Text color="gray.500" mb={4}>
                No properties found matching your filters
              </Text>
              <Button
                onClick={() => {
                  setFilterType("all");
                  setSortBy("date");
                  setSortOrder("desc");
                }}
                colorScheme="teal"
                size="sm"
              >
                Show All Properties
              </Button>
            </Box>
          )}

          {/* Load More Button (Optional) */}
          {properties.length > 0 && (
            <Box textAlign="center" mt={8}>
              <Text fontSize="sm" color="gray.500" mb={2}>
                Showing {properties.length} properties
              </Text>
            </Box>
          )}
        </Container>
      </Box>
    </>
  );
};

export default SeeAllFeaturedProperties;